import { clone } from '@jkhy/frontend-utils/functions';
import {
    UserSummaryModelDto as BoCompanyUserSummaryModelDto,
    UserStateModelDto as BoUserStateModelDto,
} from '@treasury/api/bo';
import { UserStateModelDto as ConsumerUserStateModelDto } from '@treasury/api/channel/overrides';
import { DomainEntity } from '../../../shared';
import { CompanyUserSummary, PublishState } from './company-user-summary.entity';

const nullCompanyUserState: ConsumerUserStateModelDto | BoUserStateModelDto = {
    userId: 0,
    name: '',
    isActive: false,
    isLocked: false,
    approvalRequired: false,
    pendingState: '',
    editedByUser: undefined,
    editedDate: '',
    hasPendingChanges: false,
    isEnrolled: false,
    isEditor: false,
    hasMinimumRequirements: false,
    isSuperUser: false,
    alias: '',
};

export class CompanyUserState extends DomainEntity<
    ConsumerUserStateModelDto | BoUserStateModelDto
> {
    constructor(dto = clone(nullCompanyUserState)) {
        super(dto);
    }

    public createDefault(): this {
        return new CompanyUserState(clone(nullCompanyUserState)) as this;
    }

    public get userId() {
        return this.dto.userId;
    }

    public get name() {
        return this.dto.name;
    }

    public get isActive() {
        return this.dto.isActive;
    }

    public get isLocked() {
        return this.dto.isLocked;
    }

    public get isEnrolled() {
        return this.dto.isEnrolled;
    }

    public get isEditor() {
        return this.dto.isEditor;
    }

    public get isSuperUser() {
        return this.dto.isSuperUser;
    }

    public get hasPendingChanges() {
        return this.dto.hasPendingChanges;
    }

    public get approvalRequired() {
        return this.dto.approvalRequired;
    }

    public get lastEditedOn() {
        return this.dto.editedDate ? new Date(this.dto.editedDate).toLocaleString() : undefined;
    }

    public get lastEditedBy() {
        return `${this.dto.editedByUser?.name} (${this.dto.editedByUser?.loginID})`;
    }

    public get hasMinimumRequirements() {
        return this.dto.hasMinimumRequirements;
    }

    public get alias() {
        return this.dto.alias;
    }

    public get pendingState() {
        return this.dto.pendingState;
    }

    public get isEditable() {
        return !this.isPendingApproval;
    }

    public get isNew() {
        return this.pendingState === PublishState.New;
    }

    public get isDraft() {
        return this.pendingState === PublishState.New || this.pendingState === PublishState.Edited;
    }

    public get isPendingApproval() {
        return this.isPendingApprovalBackOffice || this.isPendingApprovalChannel;
    }

    public get isPendingApprovalChannel() {
        return this.pendingState === PublishState.Pending;
    }

    public get isPendingApprovalBackOffice() {
        return this.pendingState === PublishState.FiPending;
    }

    public get isPendingApprovalAndNonApprover() {
        return (this.isPendingApprovalBackOffice && this.isEditor) || this.isPendingApprovalChannel;
    }

    public get isPendingApprovalAndApprover() {
        return this.pendingState === PublishState.FiPending && !this.isEditor;
    }

    private get publishState() {
        if (this.isPendingApproval) {
            return PublishState.Pending;
        } else if (this.isPendingApprovalBackOffice) {
            return PublishState.FiPending;
        } else if (this.isNew) {
            return PublishState.New;
        } else if (this.hasPendingChanges) {
            return PublishState.Edited;
        } else {
            return PublishState.None;
        }
    }

    public toCompanyUserSummary(): CompanyUserSummary {
        return new CompanyUserSummary({
            id: this.userId,
            publishState: this.publishState,
            userId: this.alias,
            status: this.pendingState,
            isLocked: this.isLocked,
            isAdmin: this.isEditor,
            isSuperUser: this.isSuperUser,
            isActive: this.isActive,
            isEditor: this.isEditor,
            isDeleted: false,
            isAuthenticationLocked: false,
            isAuthenticationSuspended: '',
            utcSuspendEndDate: '',
            isMfaLocked: false,
            authenticationMethodId: 0,
        } as BoCompanyUserSummaryModelDto);
    }
}
